import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import type { TentConfigV4 } from '../types';
import { formatPrice } from '../../../state/offerings';

interface RoomDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tentConfig: TentConfigV4 | null;
  pricePerNight: number;
  messages: {
    close: string;
    pricePerNight: string;
    amenities: string;
    capacity: string;
    privateBathroom: string;
    sharedBathroom: string;
  };
}

/**
 * Modal for displaying full room details with image gallery.
 */
export const RoomDetailsModal: React.FC<RoomDetailsModalProps> = ({
  isOpen,
  onClose,
  tentConfig,
  pricePerNight,
  messages,
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Reset active image when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveImageIndex(0);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handlePrevImage = useCallback(() => {
    if (!tentConfig?.gallery) return;
    setActiveImageIndex((prev) =>
      prev === 0 ? tentConfig.gallery.length - 1 : prev - 1,
    );
  }, [tentConfig?.gallery]);

  const handleNextImage = useCallback(() => {
    if (!tentConfig?.gallery) return;
    setActiveImageIndex((prev) =>
      prev === tentConfig.gallery.length - 1 ? 0 : prev + 1,
    );
  }, [tentConfig?.gallery]);

  if (!isOpen || !tentConfig) return null;

  const gallery = tentConfig.gallery.length > 0 ? tentConfig.gallery : [{ imageUrl: tentConfig.imageUrl }];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="room-details-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal content */}
      <div
        className={clsx(
          'relative w-full max-w-3xl max-h-[90vh]',
          'bg-white rounded-2xl shadow-2xl',
          'overflow-hidden',
          'animate-in fade-in zoom-in-95 duration-200',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className={clsx(
            'absolute top-4 right-4 z-10',
            'w-10 h-10 rounded-full',
            'bg-white/90 backdrop-blur-sm shadow-lg',
            'flex items-center justify-center',
            'hover:bg-white transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-emerald-500',
          )}
          aria-label={messages.close}
        >
          <CloseIcon className="w-5 h-5 text-gray-700" />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Image gallery */}
          <div className="relative aspect-[16/10] bg-gray-100">
            <img
              src={gallery[activeImageIndex].imageUrl}
              alt={`${tentConfig.title} - Image ${activeImageIndex + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Gallery navigation */}
            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevImage}
                  className={clsx(
                    'absolute left-4 top-1/2 -translate-y-1/2',
                    'w-10 h-10 rounded-full',
                    'bg-white/90 shadow-lg',
                    'flex items-center justify-center',
                    'hover:bg-white transition-colors',
                  )}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  type="button"
                  onClick={handleNextImage}
                  className={clsx(
                    'absolute right-4 top-1/2 -translate-y-1/2',
                    'w-10 h-10 rounded-full',
                    'bg-white/90 shadow-lg',
                    'flex items-center justify-center',
                    'hover:bg-white transition-colors',
                  )}
                  aria-label="Next image"
                >
                  <ChevronRightIcon className="w-5 h-5 text-gray-700" />
                </button>

                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {gallery.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={clsx(
                        'w-2 h-2 rounded-full transition-all',
                        idx === activeImageIndex
                          ? 'bg-white w-4'
                          : 'bg-white/60 hover:bg-white/80',
                      )}
                      aria-label={`Go to image ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Details content */}
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 id="room-details-title" className="text-2xl font-bold text-gray-900">
                  {tentConfig.displayName || tentConfig.title}
                </h2>
                {tentConfig.capacity && (
                  <p className="text-gray-500 flex items-center gap-2 mt-1">
                    <UsersIcon className="w-5 h-5" />
                    {messages.capacity}: {tentConfig.capacity} guests
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  {formatPrice({ amount: pricePerNight, currency: 'INR' })}
                </p>
                <p className="text-sm text-gray-500">{messages.pricePerNight}</p>
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-600 mb-6">{tentConfig.description}</p>

            {/* Amenities */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {messages.amenities}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Bathroom type */}
                <div
                  className={clsx(
                    'flex items-center gap-3 p-3 rounded-lg',
                    tentConfig.hasPrivateBathroom ? 'bg-emerald-50' : 'bg-gray-50',
                  )}
                >
                  <BathIcon
                    className={clsx(
                      'w-5 h-5',
                      tentConfig.hasPrivateBathroom ? 'text-emerald-600' : 'text-gray-500',
                    )}
                  />
                  <span
                    className={clsx(
                      'font-medium',
                      tentConfig.hasPrivateBathroom ? 'text-emerald-700' : 'text-gray-700',
                    )}
                  >
                    {tentConfig.hasPrivateBathroom
                      ? messages.privateBathroom
                      : messages.sharedBathroom}
                  </span>
                </div>

                {/* Other amenities */}
                {tentConfig.amenities.map((amenity, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg bg-gray-50"
                  >
                    <CheckIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-700">{amenity.summary}</span>
                      {amenity.detail && (
                        <p className="text-sm text-gray-500 mt-0.5">{amenity.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Icon components
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const BathIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 6h16M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"
    />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export default RoomDetailsModal;
